import { useState, useEffect, useRef } from "react";
import TranscriptionService from "../components/TranscriptionService";

/**
 * Custom hook for managing transcription functionality
 * @returns {Object} Transcription state and handlers
 */
export default function useTranscription() {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [finalTranscript, setFinalTranscript] = useState("");
  
  // Create a ref for the transcription service
  const transcriptionServiceRef = useRef(new TranscriptionService());
  
  // Effect to sync finalTranscript state with service
  useEffect(() => {
    if (transcriptionServiceRef.current) {
      transcriptionServiceRef.current.updateTranscript(finalTranscript);
    }
  }, [finalTranscript]);
  
  /**
   * Starts or stops recording
   * @param {boolean} isChatLoading - Whether chat is currently loading
   */
  const toggleRecording = (isChatLoading) => {
    if (isChatLoading) return;

    if (!isRecording && !isConnecting) {
      // Keep existing transcript - don't clear it when starting new recording
      // Only clear partial transcript, preserve finalTranscript
      transcriptionServiceRef.current.partialTranscriptRef = "";
      transcriptionServiceRef.current.startWebSocketTranscription(
        setIsConnecting,
        setIsRecording,
        setTranscript,
        setFinalTranscript
      );
    } else if (isRecording) {
      transcriptionServiceRef.current.stopWebSocketTranscription(setIsRecording);
    }
  };
  
  /**
   * Updates the transcript
   * @param {string} data - New transcript data
   */
  const updateTranscript = (data) => {
    setTranscript(data);
    // If user manually edits, update both final transcript state and ref
    if (!isRecording) {
      setFinalTranscript(data);
      transcriptionServiceRef.current.updateTranscript(data);
    }
  };
  
  /**
   * Clears the transcript
   */
  const clearTranscript = () => {
    setTranscript("");
    setFinalTranscript("");
    transcriptionServiceRef.current.clearTranscript();
  };

  /**
   * Explicitly stops recording without toggling
   */
  const stopRecording = () => {
    if (isRecording) {
      transcriptionServiceRef.current.stopWebSocketTranscription(
        setIsRecording,
        setIsConnecting
      );
    }
  };
  
  return {
    isRecording,
    isConnecting,
    transcript,
    finalTranscript,
    toggleRecording,
    stopRecording,
    updateTranscript,
    clearTranscript
  };
}
